import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateFD, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const FDCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [principal, setPrincipal] = useState(100000);
  const [rate, setRate] = useState(7);
  const [tenure, setTenure] = useState(12);
  const [compounding, setCompounding] = useState(4);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, rate, tenure, compounding]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const p = Number(principal) || 0;
    const r = Number(rate) || 0;
    const t = Number(tenure) || 0;
    const c = Number(compounding) || 4;
    const fdResult = calculateFD(p, r, t, c);
    setResult(fdResult);
  };

  const handleDownloadPDF = () => {
    const compoundingText = {
      1: 'Yearly',
      2: 'Half-Yearly',
      4: 'Quarterly',
      12: 'Monthly'
    };
    generateGenericPDF('FD Calculator Report', {
      'Principal Amount': principal,
      'Interest Rate': `${rate}% p.a.`,
      'Tenure': `${tenure} months`,
      'Compounding': compoundingText[compounding],
      'Total Interest': result.totalInterest,
      'Maturity Amount': result.maturityAmount,
    });
  };

  const handleReset = () => {
    setPrincipal(100000);
    setRate(7);
    setTenure(12);
    setCompounding(4);
  };

  const chartData = result ? {
    labels: ['Principal', 'Interest Earned'],
    datasets: [{
      data: [result.principal, result.totalInterest],
      backgroundColor: ['#f97316', '#10b981'],
      borderColor: ['#ea580c', '#059669'],
      borderWidth: 2,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="FD Calculator"
        description="Free FD Calculator India - Calculate Fixed Deposit maturity amount with compound interest. Compare returns across compounding frequencies. Export results as PDF."
        keywords="FD calculator, fixed deposit calculator, FD interest calculator, FD maturity calculator, bank FD calculator India"
        canonicalPath="/fd"
        calculatorType="FD Calculator"
      />
      <div className="calculator-header">
        <h1>FD Calculator</h1>
        <p>Calculate Fixed Deposit maturity amount with compound interest</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Principal Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
                max="100000000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="10000000"
                step="1000"
                value={principal || 0}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((principal || 0) / 10000000) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={rate === '' ? '' : rate}
              onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="15"
              step="0.1"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="15"
                step="0.1"
                value={rate || 0}
                onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((rate || 0) / 15) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Tenure (Months)</label>
            <input
              type="number"
              value={tenure === '' ? '' : tenure}
              onChange={(e) => setTenure(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="120"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="120"
                step="1"
                value={tenure || 0}
                onChange={(e) => setTenure(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((tenure || 0) / 120) * 100}%` }}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              {Math.floor((tenure || 0) / 12)} years {(tenure || 0) % 12} months
            </small>
          </div>

          <div className="input-group">
            <label>Compounding Frequency</label>
            <select
              value={compounding}
              onChange={(e) => setCompounding(Number(e.target.value))}
            >
              <option value={12}>Monthly</option>
              <option value={4}>Quarterly</option>
              <option value={2}>Half-Yearly</option>
              <option value={1}>Yearly</option>
            </select>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Maturity Amount</h3>
                <div className="amount">{formatCurrency(result.maturityAmount)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Amount</span>
                  <span className="value">{formatCurrency(result.principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(result.totalInterest)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Effective Yield</span>
                  <span className="value">
                    {((result.totalInterest / principal) * 100).toFixed(2)}%
                  </span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About Fixed Deposits</h4>
        <ul>
          <li><strong>Safety:</strong> FDs are one of the safest investment options</li>
          <li><strong>DICGC Cover:</strong> Deposits up to ₹5 lakh are insured</li>
          <li><strong>TDS:</strong> 10% TDS on interest above ₹40,000 (₹50,000 for seniors)</li>
          <li><strong>Premature Withdrawal:</strong> Usually allowed with penalty</li>
          <li><strong>Tax Saver FD:</strong> 5-year lock-in qualifies for 80C deduction</li>
        </ul>
      </div>
    </div>
  );
};

export default FDCalculator;
